/**
 * Hook para monitoramento em tempo real do status da eleição
 * Utiliza polling para verificar mudanças de status e contagem de votos
 * Implementa auto-encerramento quando quórum de 51% é atingido
 */
import { useEffect, useCallback, useRef, useState } from "react";
import { supabase } from "../config/supabase";

interface ElectionStatus {
  id: string;
  status: "configurando" | "cadastro_candidatos" | "ativa" | "encerrada";
  total_votos: number;
  total_eleitores?: number;
  percentual_participacao?: number;
  data_encerramento?: string;
  motivo_encerramento?: string;
}

interface UseElectionRealtimeOptions {
  eleicaoId: string | null;
  onStatusChange?: (newStatus: string, eleicao: ElectionStatus) => void;
  onAutoClose?: (eleicao: ElectionStatus) => void;
  onVoteCountChange?: (totalVotos: number, percentual: number) => void;
  pollingInterval?: number; // ms, default 30000 (30s)
  enabled?: boolean;
}

interface UseElectionRealtimeReturn {
  electionStatus: ElectionStatus | null;
  isPolling: boolean;
  lastCheck: Date | null;
  checkElectionStatus: () => Promise<ElectionStatus | null>;
  startPolling: () => void;
  stopPolling: () => void;
}

export const useElectionRealtime = (
  options: UseElectionRealtimeOptions,
): UseElectionRealtimeReturn => {
  const {
    eleicaoId,
    onStatusChange,
    onAutoClose,
    onVoteCountChange,
    pollingInterval = 30000,
    enabled = true,
  } = options;

  const [electionStatus, setElectionStatus] = useState<ElectionStatus | null>(
    null,
  );
  const [isPolling, setIsPolling] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousStatusRef = useRef<string | null>(null);
  const previousVotesRef = useRef<number | null>(null);

  /**
   * Verifica o status atual da eleição e conta eleitores
   */
  const checkElectionStatus =
    useCallback(async (): Promise<ElectionStatus | null> => {
      if (!eleicaoId) return null;

      try {
        // Buscar dados da eleição
        const { data: eleicao, error: eleicaoError } = await supabase
          .from("cp_eleicoes")
          .select("id, status, total_votos, data_fim")
          .eq("id", eleicaoId)
          .single();

        if (eleicaoError || !eleicao) {
          console.error(
            "❌ [useElectionRealtime] Erro ao buscar eleição:",
            eleicaoError,
          );
          return null;
        }

        // Contar total de eleitores autorizados
        const { count: totalEleitores } = await supabase
          .from("cp_eleitores_autorizados")
          .select("*", { count: "exact", head: true })
          .eq("eleicao_id", eleicaoId);

        const percentualParticipacao =
          totalEleitores && totalEleitores > 0
            ? (eleicao.total_votos / totalEleitores) * 100
            : 0;

        const status: ElectionStatus = {
          id: eleicao.id,
          status: eleicao.status,
          total_votos: eleicao.total_votos || 0,
          total_eleitores: totalEleitores || 0,
          percentual_participacao: percentualParticipacao,
          data_encerramento: eleicao.data_fim,
          //motivo_encerramento: eleicao.motivo_encerramento,
        };

        setElectionStatus(status);
        setLastCheck(new Date());

        // Verificar se status mudou
        if (
          previousStatusRef.current &&
          previousStatusRef.current !== status.status
        ) {
          console.log(
            `🔄 [useElectionRealtime] Status mudou: ${previousStatusRef.current} → ${status.status}`,
          );
          onStatusChange?.(status.status, status);

          // Se eleição foi encerrada, notificar
          if (status.status === "encerrada") {
            onAutoClose?.(status);
          }
        }
        previousStatusRef.current = status.status;

        // Verificar se contagem de votos mudou
        if (
          previousVotesRef.current !== null &&
          previousVotesRef.current !== status.total_votos
        ) {
          console.log(
            `🗳️ [useElectionRealtime] Votos: ${previousVotesRef.current} → ${status.total_votos} (${percentualParticipacao.toFixed(1)}%)`,
          );
          onVoteCountChange?.(status.total_votos, percentualParticipacao);
        }
        previousVotesRef.current = status.total_votos;

        return status;
      } catch (error) {
        console.error("❌ [useElectionRealtime] Erro no polling:", error);
        return null;
      }
    }, [eleicaoId, onStatusChange, onAutoClose, onVoteCountChange]);

  /**
   * Inicia o polling
   */
  const startPolling = useCallback(() => {
    if (!eleicaoId || !enabled) return;

    // Limpar intervalo existente
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setIsPolling(true);
    console.log(
      `🔄 [useElectionRealtime] Iniciando polling a cada ${pollingInterval / 1000}s para eleição ${eleicaoId}`,
    );

    // Verificar imediatamente
    checkElectionStatus();

    // Configurar intervalo
    intervalRef.current = setInterval(() => {
      checkElectionStatus();
    }, pollingInterval);
  }, [eleicaoId, enabled, pollingInterval, checkElectionStatus]);

  /**
   * Para o polling
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
    console.log("⏹️ [useElectionRealtime] Polling parado");
  }, []);

  // Iniciar/parar polling quando eleicaoId ou enabled mudar
  useEffect(() => {
    if (eleicaoId && enabled) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [eleicaoId, enabled, startPolling, stopPolling]);

  return {
    electionStatus,
    isPolling,
    lastCheck,
    checkElectionStatus,
    startPolling,
    stopPolling,
  };
};

/**
 * Função auxiliar para verificar e auto-encerrar eleição quando quórum é atingido
 * ou quando prazo de 15 dias corridos foi ultrapassado
 * Chamada após cada voto registrado
 */
export const verificarAutoEncerramentoEleicao = async (
  eleicaoId: string,
): Promise<boolean> => {
  try {
    // Buscar dados atuais da eleição
    const { data: eleicao, error: eleicaoError } = await supabase
      .from("cp_eleicoes")
      .select("id, status, total_votos, data_inicio")
      .eq("id", eleicaoId)
      .single();

    if (eleicaoError || !eleicao) {
      console.error(
        "❌ [verificarAutoEncerramento] Erro ao buscar eleição:",
        eleicaoError,
      );
      return false;
    }

    // Se já encerrada, não fazer nada
    if (eleicao.status === "encerrada") {
      return false;
    }

    // Verificar prazo de 15 dias corridos (NR-05)
    if (eleicao.data_inicio) {
      const dataInicio = new Date(eleicao.data_inicio);
      const dataAtual = new Date();
      const diasDecorridos = Math.floor(
        (dataAtual.getTime() - dataInicio.getTime()) / (1000 * 60 * 60 * 24),
      );

      console.log(
        `📅 [verificarAutoEncerramento] Dias desde início: ${diasDecorridos}/15`,
      );

      // Se passou 15 dias corridos, encerrar automaticamente
      if (diasDecorridos >= 15) {
        console.log(
          "⏰ [verificarAutoEncerramento] Prazo de 15 dias atingido! Encerrando eleição...",
        );

        const { error: updateError } = await supabase
          .from("cp_eleicoes")
          .update({
            status: "encerrada",
            data_fim: new Date().toISOString(),
          })
          .eq("id", eleicaoId);

        if (updateError) {
          console.error(
            "❌ [verificarAutoEncerramento] Erro ao encerrar eleição por prazo:",
            updateError,
          );
          return false;
        }

        console.log(
          "🎉 [verificarAutoEncerramento] Eleição encerrada automaticamente por prazo (15 dias)!",
        );
        return true;
      }
    }

    // Contar total de eleitores
    const { count: totalEleitores } = await supabase
      .from("cp_eleitores_autorizados")
      .select("*", { count: "exact", head: true })
      .eq("eleicao_id", eleicaoId);

    if (!totalEleitores || totalEleitores === 0) {
      console.warn("⚠️ [verificarAutoEncerramento] Nenhum eleitor cadastrado");
      return false;
    }

    const percentual = (eleicao.total_votos / totalEleitores) * 100;
    console.log(
      `📊 [verificarAutoEncerramento] Participação: ${percentual.toFixed(1)}% (${eleicao.total_votos}/${totalEleitores})`,
    );

    // Verificar se atingiu 51%
    if (percentual >= 51) {
      console.log(
        "✅ [verificarAutoEncerramento] Quórum de 51% atingido! Encerrando eleição...",
      );

      const { error: updateError } = await supabase
        .from("cp_eleicoes")
        .update({
          status: "encerrada",
          data_fim: new Date().toISOString(),
          //motivo_encerramento: `Quórum de 51% atingido automaticamente (${percentual.toFixed(1)}% de participação)`,
        })
        .eq("id", eleicaoId);

      if (updateError) {
        console.error(
          "❌ [verificarAutoEncerramento] Erro ao encerrar eleição:",
          updateError,
        );
        return false;
      }

      console.log(
        "🎉 [verificarAutoEncerramento] Eleição encerrada com sucesso por quórum!",
      );
      return true;
    }

    return false;
  } catch (error) {
    console.error("❌ [verificarAutoEncerramento] Erro:", error);
    return false;
  }
};

export default useElectionRealtime;
